const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect } from 'react';
import {
  User as UserIcon,
  Heart,
  MapPin,
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  Package,
  Tag,
  UserCog,
  Plus,
  List,
  Users,
  Globe,
  DollarSign,
  Building,
} from 'lucide-react';

const topNavItems = [
  { label: 'Philippines', path: '#' },
  { label: 'Contact', path: '#contact' },
  { label: 'Services', path: '#services' },
  { label: 'About', path: '#about' },
];

const STATIC_NAV = [{ label: 'Shop', path: '#shop' }];

const Navbar = ({
  onNavigate,
  user,
  onLogout,
  activePage,
  wishlist = [],
  onOpenWishlist,
  categories = [],
}) => {
  const mainNavItems = [
    ...STATIC_NAV,
    ...categories.map((cat) => ({
      label: cat.charAt(0).toUpperCase() + cat.slice(1),
      path: `#${cat}`,
    })),
  ];
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isAuthenticated = !!user;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLocationClick = (e) => {
    if (e) e.preventDefault();
    onNavigate('contact');
    setIsMobileMenuOpen(false);
    window.history.pushState(null, '', '/contact');
    setTimeout(() => {
      const mapSection = document.getElementById('location-map');
      if (mapSection) {
        mapSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleNavClick = (e, item) => {
    e.preventDefault();

    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    let targetPage = '';

    switch (item.label) {
      case 'Contact':
        targetPage = 'contact';
        break;
      case 'Services':
        targetPage = 'services';
        break;
      case 'About':
        targetPage = 'about';
        break;
      case 'Philippines':
        handleLocationClick();
        return;
      case 'Shop':
        targetPage = 'shop';
        break;
      default:
        // All dynamic categories navigate to shop with category filter
        targetPage = item.label.toLowerCase();
        break;
    }

    onNavigate(targetPage);
    window.history.pushState(null, '', targetPage === 'home' ? '/' : `/${targetPage}`);
  };

  const handleLogoClick = () => {
    onNavigate('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
    window.history.pushState(null, '', '/');
  };

  const handleAuthClick = () => {
    if (!isAuthenticated) {
      onNavigate('auth');
      window.history.pushState(null, '', '/auth');
    } else if (user?.role && user.role !== 'member') {
      if (user.role === 'admin') {
        onNavigate('admin-dashboard');
        window.history.pushState(null, '', '/admin/dashboard');
      } else {
        onNavigate('admin-profile');
        window.history.pushState(null, '', '/admin/profile');
      }
    } else {
      onNavigate('profile');
      window.history.pushState(null, '', '/profile');
    }
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getDisplayName = () => {
    if (!user) return 'Guest';

    if (user.role && user.role !== 'member') return user.role === 'admin' ? 'Admin' : 'Staff';

    if (user.firstName && user.firstName !== '') {
      if (user.lastName && user.lastName !== '') {
        return `${user.firstName} ${user.lastName.charAt(0)}.`;
      }
      return user.firstName;
    }

    if (user.name && user.name !== 'Member' && user.name !== 'New Member') {
      const nameParts = user.name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts[1] : '';

      if (firstName && firstName.length > 0) {
        if (lastName && lastName.length > 0) {
          return `${firstName} ${lastName.charAt(0)}.`;
        }
        return firstName.length <= 12 ? firstName : firstName.substring(0, 10) + '...';
      }

      return user.name.length <= 12 ? user.name : user.name.substring(0, 10) + '...';
    }

    if (user.email) {
      return user.email.split('@')[0];
    }

    return 'Member';
  };

  const wishlistTotalQty = wishlist.reduce((sum, item) => sum + (item.quantity || 1), 0);

  const isItemActive = (label) => {
    if (label === 'Shop' && activePage === 'shop') return true;
    if (label.toLowerCase() === activePage) return true;
    if (label === 'About' && activePage === 'about') return true;
    if (label === 'Contact' && activePage === 'contact') return true;
    if (label === 'Services' && activePage === 'services') return true;
    return false;
  };

  // Render user menu - ULTRA HIGH Z-INDEX
  const renderUserMenu = () => {
    if (!user) return null;

    if (user.role && user.role !== 'member') {
      const isAdmin = user.role === 'admin';
      const perms = Array.isArray(user.permissions) ? user.permissions : [];
      const can = (...ps) => isAdmin || ps.some((p) => perms.includes(p));

      const inventoryItems = [
        can('view_products', 'manage_products', 'manage_stock') &&
          React.createElement(
            'button',
            {
              key: 'products',
              onClick: () => {
                onNavigate('admin-products');
                window.history.pushState(null, '', '/admin/products');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(List, { size: 14 }),
            ' Products List'
          ),
        can('manage_products') &&
          React.createElement(
            'button',
            {
              key: 'add-product',
              onClick: () => {
                onNavigate('admin-add-product');
                window.history.pushState(null, '', '/admin/add-product');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(Plus, { size: 14 }),
            ' Add Product'
          ),
        can('view_gold_rates', 'update_gold_rates') &&
          React.createElement(
            'button',
            {
              key: 'pricing',
              onClick: () => {
                onNavigate('admin-pricing');
                window.history.pushState(null, '', '/admin/pricing');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(Tag, { size: 14 }),
            ' Jewelry Pricing'
          ),
      ].filter(Boolean);

      const managementItems = [
        can(
          'view_orders',
          'update_order_status',
          'add_timeline',
          'verify_payments',
          'reject_payments'
        ) &&
          React.createElement(
            'button',
            {
              key: 'transactions',
              onClick: () => {
                onNavigate('admin-transactions');
                window.history.pushState(null, '', '/admin/transactions');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(DollarSign, { size: 14 }),
            ' Transactions'
          ),
        can('view_members') &&
          React.createElement(
            'button',
            {
              key: 'members',
              onClick: () => {
                onNavigate('admin-members');
                window.history.pushState(null, '', '/admin/members');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(Users, { size: 14 }),
            ' Members'
          ),
        can('manage_bank_details') &&
          React.createElement(
            'button',
            {
              key: 'bank',
              onClick: () => {
                onNavigate('admin-bank-details');
                window.history.pushState(null, '', '/admin/bank-details');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(Building, { size: 14 }),
            ' Bank Details'
          ),
        can('manage_settings') &&
          React.createElement(
            'button',
            {
              key: 'settings',
              onClick: () => {
                onNavigate('admin-settings');
                window.history.pushState(null, '', '/admin/settings');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(Globe, { size: 14 }),
            ' Site Settings'
          ),
        can('manage_staff') &&
          React.createElement(
            'button',
            {
              key: 'staff',
              onClick: () => {
                onNavigate('admin-staff');
                window.history.pushState(null, '', '/admin/staff');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(Users, { size: 14 }),
            ' Staff Management'
          ),
      ].filter(Boolean);

      return React.createElement(
        'div',
        {
          className:
            'bg-white border border-stone-200 shadow-2xl rounded-sm py-2 w-64 text-left animate-fade-in',
          style: { zIndex: 999999, position: 'relative' },
        },
        React.createElement('div', {
          className:
            'absolute -top-2 right-4 w-4 h-4 bg-white border-t border-l border-stone-200 transform rotate-45',
          style: { zIndex: 999999 },
        }),
        React.createElement(
          'div',
          { className: 'relative bg-white rounded-sm', style: { zIndex: 999999 } },
          React.createElement(
            'div',
            { className: 'px-4 py-3 border-b border-stone-100 mb-2 bg-stone-50/50' },
            React.createElement(
              'span',
              { className: 'block text-[10px] uppercase tracking-widest text-gold-600 font-bold' },
              isAdmin ? 'Admin Console' : 'Staff Console'
            ),
            React.createElement(
              'span',
              { className: 'block text-sm font-serif text-stone-900 mt-1' },
              isAdmin ? 'Administrator' : getDisplayName()
            ),
            !isAdmin &&
              React.createElement(
                'span',
                { className: 'block text-[10px] text-stone-400 mt-0.5 capitalize' },
                user.role
              )
          ),

          can('view_dashboard', 'view_reports') &&
            React.createElement(
              'button',
              {
                onClick: () => {
                  onNavigate('admin-dashboard');
                  window.history.pushState(null, '', '/admin/dashboard');
                },
                className:
                  'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
              },
              React.createElement(LayoutDashboard, { size: 14, className: 'text-gold-600' }),
              ' Dashboard'
            ),

          inventoryItems.length > 0 &&
            React.createElement(
              React.Fragment,
              null,
              React.createElement('div', { className: 'my-2 border-t border-stone-100' }),
              React.createElement(
                'span',
                {
                  className:
                    'px-4 py-1 text-[9px] uppercase tracking-widest text-stone-400 font-semibold',
                },
                'Inventory'
              ),
              inventoryItems
            ),

          managementItems.length > 0 &&
            React.createElement(
              React.Fragment,
              null,
              React.createElement('div', { className: 'my-2 border-t border-stone-100' }),
              React.createElement(
                'span',
                {
                  className:
                    'px-4 py-1 text-[9px] uppercase tracking-widest text-stone-400 font-semibold',
                },
                'Management'
              ),
              managementItems
            ),

          React.createElement('div', { className: 'my-2 border-t border-stone-100' }),
          React.createElement(
            'span',
            {
              className:
                'px-4 py-1 text-[9px] uppercase tracking-widest text-stone-400 font-semibold',
            },
            'Account'
          ),
          React.createElement(
            'button',
            {
              onClick: () => {
                onNavigate('admin-profile');
                window.history.pushState(null, '', '/admin/profile');
              },
              className:
                'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            },
            React.createElement(UserCog, { size: 14 }),
            ' Edit Profile'
          ),
          React.createElement(
            'button',
            {
              onClick: () => {
                onLogout();
                window.history.pushState(null, '', '/');
              },
              className:
                'w-full text-left px-4 py-3 text-xs text-red-500 hover:bg-red-50 flex items-center gap-3 transition-colors mt-2 border-t border-stone-100',
            },
            React.createElement(LogOut, { size: 14 }),
            ' Sign Out'
          )
        )
      );
    }

    // Member Menu
    return React.createElement(
      'div',
      {
        className:
          'bg-white border border-stone-200 shadow-2xl rounded-sm py-2 w-56 text-left animate-fade-in',
        style: { zIndex: 999999, position: 'relative' },
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 266 },
      },
      React.createElement('div', {
        className:
          'absolute -top-2 right-4 w-4 h-4 bg-white border-t border-l border-stone-200 transform rotate-45',
        style: { zIndex: 999999 },
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 267 },
      }),

      React.createElement(
        'div',
        {
          className: 'relative bg-white rounded-sm',
          style: { zIndex: 999999 },
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 269 },
        },
        React.createElement(
          'div',
          {
            className: 'px-4 py-3 border-b border-stone-100 mb-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 270 },
          },
          React.createElement(
            'span',
            {
              className: 'block text-[10px] uppercase tracking-widest text-gold-600 font-bold',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 271 },
            },
            'Member'
          ),
          React.createElement(
            'span',
            {
              className: 'block text-sm font-serif text-stone-900 mt-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 272 },
            },
            getDisplayName()
          ),
          _optionalChain([user, 'optionalAccess', (_2) => _2.email]) &&
            React.createElement(
              'span',
              {
                className: 'block text-[10px] text-stone-400 mt-1 truncate',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 276 },
              },
              user.email
            )
        ),

        React.createElement(
          'button',
          {
            onClick: () => {
              onNavigate('profile');
              window.history.pushState(null, '', '/profile');
            },
            className:
              'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 280 },
          },
          React.createElement(UserCog, {
            size: 14,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 284 },
          }),
          ' Account Settings'
        ),
        React.createElement(
          'button',
          {
            onClick: () => {
              onNavigate('orders');
              window.history.pushState(null, '', '/orders');
            },
            className:
              'w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 286 },
          },
          React.createElement(Package, {
            size: 14,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 290 },
          }),
          ' My Orders'
        ),

        React.createElement(
          'button',
          {
            onClick: () => {
              onLogout();
              window.history.pushState(null, '', '/');
            },
            className:
              'w-full text-left px-4 py-3 text-xs text-red-500 hover:bg-red-50 flex items-center gap-3 transition-colors mt-2 border-t border-stone-100',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 293 },
          },

          React.createElement(LogOut, {
            size: 14,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 300 },
          }),
          ' Sign Out'
        )
      )
    );
  };

  return React.createElement(
    React.Fragment,
    null,
    React.createElement(
      'nav',
      {
        className: `fixed top-0 w-full z-40 bg-white border-b border-stone-200 transition-all duration-300 overflow-visible ${
          isScrolled ? 'shadow-md' : ''
        }`,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 309 },
      },

      React.createElement(
        'div',
        {
          className: 'max-w-[1440px] mx-auto px-6 overflow-visible',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 314 },
        },

        /* TOP ROW */
        React.createElement(
          'div',
          {
            className: 'h-24 flex justify-between items-center relative overflow-visible',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 317 },
          },

          /* Left: Secondary Nav (Desktop) */
          React.createElement(
            'div',
            {
              className: 'hidden lg:flex items-center gap-8',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 320 },
            },
            topNavItems.map((item) =>
              React.createElement(
                'a',
                {
                  key: item.label,
                  href: item.path,
                  onClick: (e) => handleNavClick(e, item),
                  className: `text-[10px] tracking-[0.2em] uppercase font-medium transition-colors ${
                    isItemActive(item.label)
                      ? 'text-gold-600 font-bold'
                      : 'text-stone-500 hover:text-stone-900'
                  }`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 322 },
                },

                item.label
              )
            )
          ),

          /* Mobile Menu Toggle - Left side */
          React.createElement(
            'button',
            {
              className: 'lg:hidden text-stone-800 p-2',
              onClick: () => setIsMobileMenuOpen(!isMobileMenuOpen),
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 336 },
            },

            isMobileMenuOpen
              ? React.createElement(X, {
                  size: 24,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 340 },
                })
              : React.createElement(Menu, {
                  size: 24,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 340 },
                })
          ),

          /* Center: Logo */
          React.createElement(
            'div',
            {
              className: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 344 },
            },
            React.createElement(
              'h1',
              {
                onClick: handleLogoClick,
                className:
                  'font-serif text-3xl md:text-4xl text-stone-900 tracking-wide cursor-pointer hover:opacity-80 transition-opacity',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 345 },
              },
              'Sparkling Gems'
            )
          ),

          /* Right: Action Icons */
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 354 },
            },

            /* Desktop Only: Wishlist Icon */
            React.createElement(
              'button',
              {
                onClick: onOpenWishlist,
                className:
                  'flex w-10 h-10 border border-stone-200 rounded items-center justify-center text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all relative',
                title: 'Wishlist',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 359 },
              },

              React.createElement(Heart, {
                size: 18,
                strokeWidth: 1.5,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 364 },
              }),
              wishlistTotalQty > 0 &&
                React.createElement(
                  'span',
                  {
                    className:
                      'absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 366 },
                  },
                  wishlistTotalQty
                )
            ),

            /* Desktop Only: Map / Location Icon */
            React.createElement(
              'button',
              {
                onClick: () => handleLocationClick(),
                className:
                  'hidden md:flex w-10 h-10 border border-stone-200 rounded items-center justify-center text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all',
                title: 'Find Jewelry Shop',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 373 },
              },

              React.createElement(MapPin, {
                size: 18,
                strokeWidth: 1.5,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 378 },
              })
            ),

            /* PROFILE / LOGIN BUTTON */
            React.createElement(
              'div',
              {
                className: 'relative group',
                style: { overflow: 'visible' },
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 382 },
              },
              React.createElement(
                'button',
                {
                  onClick: handleAuthClick,
                  className: `flex w-10 h-10 border rounded items-center justify-center transition-all ${isAuthenticated ? 'bg-stone-900 text-gold-400 border-stone-900' : 'border-stone-200 text-stone-700 hover:bg-stone-50'}`,
                  title: isAuthenticated ? 'Account' : 'Member Login',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 383 },
                },

                React.createElement(UserIcon, {
                  size: 18,
                  strokeWidth: 1.5,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 388 },
                })
              ),

              /* Dropdown Menu - ULTRA HIGH Z-INDEX */
              isAuthenticated &&
                React.createElement(
                  'div',
                  {
                    className:
                      'absolute right-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300',
                    style: { zIndex: 999999 },
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 393 },
                  },
                  renderUserMenu()
                )
            )
          )
        ),

        /* DIVIDER */
        React.createElement('div', {
          className: 'border-t border-stone-100 hidden lg:block',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 402 },
        }),

        /* BOTTOM ROW: Main Nav */
        React.createElement(
          'div',
          {
            className: `hidden lg:flex justify-between items-center transition-all duration-300 ${isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-14 opacity-100'}`,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 405 },
          },
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-10',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 406 },
            },
            mainNavItems.map((item) =>
              React.createElement(
                'a',
                {
                  key: item.label,
                  href: item.path,
                  onClick: (e) => handleNavClick(e, item),
                  className: `text-[11px] tracking-[0.2em] uppercase font-semibold transition-colors ${
                    isItemActive(item.label)
                      ? 'text-gold-600 font-bold border-b-2 border-gold-600 pb-1'
                      : 'text-stone-600 hover:text-stone-900'
                  }`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 408 },
                },

                item.label
              )
            )
          ),

          React.createElement(
            'div',
            {
              className: 'relative group',
              style: { overflow: 'visible' },
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 421 },
            },
            React.createElement(
              'button',
              {
                className: `text-xs font-light cursor-pointer transition-colors flex items-center gap-2 ${isAuthenticated ? 'text-stone-900 font-bold' : 'text-stone-400 hover:text-gold-600'}`,
                onClick: () => !isAuthenticated && handleAuthClick(),
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 422 },
              },

              getDisplayName()
            ),

            /* Dropdown Menu attached to Name Text - ULTRA HIGH Z-INDEX */
            isAuthenticated &&
              React.createElement(
                'div',
                {
                  className:
                    'absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300',
                  style: { zIndex: 999999 },
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 431 },
                },
                renderUserMenu()
              )
          )
        )
      ),

      /* Mobile Menu Overlay */
      React.createElement(
        'div',
        {
          className: `fixed inset-0 bg-white z-30 transform transition-transform duration-500 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:hidden top-[96px] overflow-y-auto`,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 441 },
        },
        React.createElement(
          'div',
          {
            className: 'flex flex-col p-8 space-y-6 h-full border-t border-stone-100',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 442 },
          },
          React.createElement(
            'div',
            {
              className: 'space-y-4 pb-6 border-b border-stone-100',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 443 },
            },
            mainNavItems.map((item) =>
              React.createElement(
                'a',
                {
                  key: item.label,
                  href: item.path,
                  onClick: (e) => handleNavClick(e, item),
                  className: `block font-serif text-2xl ${isItemActive(item.label) ? 'text-gold-600' : 'text-stone-900'}`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 445 },
                },

                item.label
              )
            )
          ),

          React.createElement(
            'div',
            {
              className: 'space-y-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 456 },
            },
            topNavItems.map((item) =>
              React.createElement(
                'a',
                {
                  key: item.label,
                  href: item.path,
                  onClick: (e) => handleNavClick(e, item),
                  className: `block text-xs tracking-[0.2em] uppercase ${isItemActive(item.label) ? 'text-gold-600 font-bold' : 'text-stone-500'}`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 458 },
                },

                item.label
              )
            )
          ),

          /* Mobile Menu Buttons */
          React.createElement(
            'div',
            {
              className: 'pt-8 mt-auto grid grid-cols-2 gap-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 470 },
            },
            React.createElement(
              'button',
              {
                onClick: () => {
                  _optionalChain([onOpenWishlist, 'optionalCall', (_3) => _3()]);
                  setIsMobileMenuOpen(false);
                },
                className: 'flex flex-col items-center gap-2 text-stone-600 relative',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 473 },
              },
              React.createElement(Heart, {
                size: 20,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 474 },
              }),
              wishlistTotalQty > 0 &&
                React.createElement(
                  'span',
                  {
                    className:
                      'absolute top-0 right-4 bg-red-500 text-white text-[8px] w-3 h-3 flex items-center justify-center rounded-full',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 476 },
                  },
                  wishlistTotalQty
                ),
              React.createElement(
                'span',
                {
                  className: 'text-[10px] uppercase',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 480 },
                },
                'Wishlist'
              )
            ),

            React.createElement(
              'button',
              {
                onClick: () => handleLocationClick(),
                className: 'flex flex-col items-center gap-2 text-stone-600',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 483 },
              },

              React.createElement(MapPin, {
                size: 20,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 487 },
              }),
              React.createElement(
                'span',
                {
                  className: 'text-[10px] uppercase',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 488 },
                },
                'Stores'
              )
            ),

            React.createElement(
              'button',
              {
                onClick: () => {
                  if (isAuthenticated) {
                    if (
                      _optionalChain([user, 'optionalAccess', (_4) => _4.role]) &&
                      _optionalChain([user, 'optionalAccess', (_4) => _4.role]) !== 'member'
                    ) {
                      onNavigate('admin-dashboard');
                    } else {
                      onNavigate('profile');
                    }
                  } else {
                    onNavigate('auth');
                  }
                  setIsMobileMenuOpen(false);
                },
                className: 'flex flex-col items-center gap-2 text-stone-600',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 491 },
              },

              isAuthenticated
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(UserIcon, {
                      size: 20,
                      className: 'text-gold-600',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 508 },
                    }),
                    React.createElement(
                      'span',
                      {
                        className: 'text-[10px] uppercase',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 509 },
                      },
                      'Profile'
                    )
                  )
                : React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(UserIcon, {
                      size: 20,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 513 },
                    }),
                    React.createElement(
                      'span',
                      {
                        className: 'text-[10px] uppercase',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 514 },
                      },
                      'Login'
                    )
                  )
            )
          )
        )
      )
    )
  );
};

export default Navbar;
